import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  alpha,
  Chip,
  Collapse,
  Fade,
  Grow,
  keyframes,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  RadioButtonUnchecked as PendingIcon,
  AutoAwesome as AIIcon,
  Memory as ModelIcon,
  Terminal as LogIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

export interface ProcessingStep {
  label: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  duration?: number;
  logs?: string[];
}

interface ProcessingStatusProps {
  isProcessing: boolean;
  currentStep: number;
  steps: ProcessingStep[];
  elapsedTime: number;
  estimatedTime?: number;
}

// Keyframe animations
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(139, 92, 246, 0.4), 0 0 20px rgba(139, 92, 246, 0.1); }
  50% { box-shadow: 0 0 16px rgba(139, 92, 246, 0.6), 0 0 40px rgba(139, 92, 246, 0.2); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeSlideIn = keyframes`
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
`;

const scanLine = keyframes`
  0% { top: 0%; opacity: 0; }
  10% { opacity: 0.6; }
  90% { opacity: 0.6; }
  100% { top: 100%; opacity: 0; }
`;

const MODELS = [
  { name: 'Gemini 2.5 Flash', type: 'LLM', icon: '🧠' },
  { name: 'Document AI', type: 'OCR', icon: '📄' },
];

const ProcessingStatus: React.FC<ProcessingStatusProps> = ({
  isProcessing,
  currentStep,
  steps,
  elapsedTime,
  estimatedTime,
}) => {
  const [expandedLogs, setExpandedLogs] = useState<Record<number, boolean>>({});
  const [logLines, setLogLines] = useState<string[]>([]);
  const logContainerRef = useRef<HTMLDivElement>(null);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Auto-generate processing log lines based on current step
  useEffect(() => {
    if (!isProcessing) return;

    const activeStep = steps.find(s => s.status === 'running');
    if (!activeStep) return;

    const logMessages: Record<string, string[]> = {
      'Document Agent': [
        '[🤖 Orchestrator] Initializing document processing pipeline...',
        '[📄 Document Agent] Agent spawned → analyzing document metadata...',
        '[📄 Document Agent] Connecting to Firebase storage service...',
        '[📄 Document Agent] Downloading document from GCS bucket...',
        '[📄 Document Agent] Document loaded successfully (validating format)...',
        '[📄 Document Agent] Document validated → delegating to OCR Extraction Agent...',
        '[🤖 Orchestrator] Handoff: Document Agent → OCR Extraction Agent',
      ],
      'OCR Extraction Agent': [
        '[🔍 OCR Extraction Agent] Agent spawned → initializing vision pipeline...',
        '[🔍 OCR Extraction Agent] Loading Gemini 2.5 Flash model via Vertex AI...',
        '[🔍 OCR Extraction Agent] Sending document to vision model for text extraction...',
        '[🔍 OCR Extraction Agent] Processing document pages with multi-modal analysis...',
        '[🔍 OCR Extraction Agent] Extracting structured fields from raw OCR output...',
        '[🔍 OCR Extraction Agent] Normalizing field values and detecting language...',
        '[🔍 OCR Extraction Agent] Translating non-English fields to English...',
        '[🔍 OCR Extraction Agent] Extraction complete → delegating to ERP Comparison Agent...',
        '[🤖 Orchestrator] Handoff: OCR Extraction Agent → ERP Comparison Agent',
      ],
      'ERP Comparison Agent': [
        '[📊 ERP Comparison Agent] Agent spawned → loading ERP reference data...',
        '[📊 ERP Comparison Agent] Connecting to jbs-ocr-extraction-bucket...',
        '[📊 ERP Comparison Agent] Parsing XLSX sheets: Balance_Sheet, Financial_Stmt...',
        '[📊 ERP Comparison Agent] Auto-detecting document type for smart matching...',
        '[📊 ERP Comparison Agent] Initializing LLM-powered field matcher (Gemini 2.5 Flash)...',
        '[📊 ERP Comparison Agent] Comparing document fields with ERP records...',
        '[📊 ERP Comparison Agent] Analyzing value discrepancies across fields...',
        '[📊 ERP Comparison Agent] Comparison complete → delegating to Classification Agent...',
        '[🤖 Orchestrator] Handoff: ERP Comparison Agent → Classification Agent',
      ],
      'Classification Agent': [
        '[🏷️ Classification Agent] Agent spawned → analyzing comparison results...',
        '[🏷️ Classification Agent] Evaluating match rate and critical field status...',
        '[🏷️ Classification Agent] Running exception detection rules...',
        '[🏷️ Classification Agent] Computing confidence score...',
        '[🏷️ Classification Agent] Generating classification report...',
        '[🏷️ Classification Agent] Classification complete → returning results to Orchestrator...',
        '[🤖 Orchestrator] All agents completed. Pipeline finished successfully. ✅',
      ],
    };

    const msgs = logMessages[activeStep.label] || [`[Processing] ${activeStep.description}`];
    let lineIndex = 0;

    const interval = setInterval(() => {
      if (lineIndex < msgs.length) {
        const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false });
        setLogLines(prev => [...prev, `${timestamp}  ${msgs[lineIndex]}`]);
        lineIndex++;
      }
    }, 800);

    return () => clearInterval(interval);
  }, [isProcessing, steps, currentStep]);

  // Auto-scroll log container
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logLines]);

  // Reset logs when processing starts
  useEffect(() => {
    if (isProcessing && steps.every(s => s.status === 'pending' || s.status === 'running')) {
      setLogLines([]);
    }
  }, [isProcessing]);

  // Add completed/error logs
  useEffect(() => {
    steps.forEach((step, index) => {
      if (step.status === 'completed' && step.duration) {
        const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false });
        const msg = `${timestamp}  [DONE] ${step.label} completed in ${step.duration}s`;
        setLogLines(prev => {
          if (!prev.includes(msg)) return [...prev, msg];
          return prev;
        });
      }
    });
  }, [steps]);

  const completedSteps = steps.filter(s => s.status === 'completed').length;
  const totalSteps = steps.length;
  const progressPercent = (completedSteps / totalSteps) * 100;

  if (!isProcessing && steps.every(s => s.status === 'pending')) {
    return null;
  }

  const allCompleted = steps.every(s => s.status === 'completed');

  return (
    <Fade in timeout={400}>
      <Box
        sx={{
          mt: 3,
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          maxHeight: '60vh',
          overflowY: 'auto',
          border: '1px solid',
          borderColor: allCompleted
            ? alpha('#10B981', 0.4)
            : alpha('#8B5CF6', 0.3),
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha('#0F172A', 0.95)} 0%, ${alpha('#1E1B4B', 0.95)} 100%)`
              : `linear-gradient(135deg, ${alpha('#F8FAFC', 0.98)} 0%, ${alpha('#EDE9FE', 0.98)} 100%)`,
          animation: isProcessing ? `${pulseGlow} 3s ease-in-out infinite` : 'none',
          transition: 'all 0.5s ease',
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#8B5CF6', 0.3),
            borderRadius: 3,
            '&:hover': {
              background: alpha('#8B5CF6', 0.5),
            },
          },
        }}
      >
        {/* Animated scan line effect during processing */}
        {isProcessing && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.6), transparent)',
              animation: `${scanLine} 2.5s linear infinite`,
              zIndex: 1,
            }}
          />
        )}

        {/* Header Section */}
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          {/* Left: Title + Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Animated icon */}
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: allCompleted
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                boxShadow: allCompleted
                  ? '0 4px 15px rgba(16, 185, 129, 0.3)'
                  : '0 4px 15px rgba(139, 92, 246, 0.3)',
                transition: 'all 0.5s ease',
              }}
            >
              {allCompleted ? (
                <CompletedIcon sx={{ color: '#fff', fontSize: 24 }} />
              ) : (
                <AIIcon
                  sx={{
                    color: '#fff',
                    fontSize: 24,
                    animation: isProcessing ? 'spin 3s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              )}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                {allCompleted ? 'All Agents Completed' : 'Agents Processing Document'}
                {isProcessing && (
                  <Box component="span" sx={{ display: 'inline-flex', ml: 0.5, gap: '3px' }}>
                    {[0, 1, 2].map(i => (
                      <Box
                        key={i}
                        component="span"
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          backgroundColor: '#8B5CF6',
                          display: 'inline-block',
                          animation: `${dotPulse} 1.4s ease-in-out ${i * 0.2}s infinite`,
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {completedSteps}/{totalSteps} steps
                </Typography>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Elapsed: <strong>{formatTime(elapsedTime)}</strong>
                </Typography>
                {estimatedTime && isProcessing && (
                  <>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Est: ~{formatTime(estimatedTime)}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* Right: Models Used */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Models
            </Typography>
            {MODELS.map((model) => (
              <Chip
                key={model.name}
                icon={<ModelIcon sx={{ fontSize: '14px !important' }} />}
                label={`${model.icon} ${model.name}`}
                size="small"
                variant="outlined"
                sx={{
                  height: 26,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  borderColor: alpha('#8B5CF6', 0.3),
                  color: '#A78BFA',
                  '& .MuiChip-icon': { color: '#A78BFA' },
                  background: alpha('#8B5CF6', 0.06),
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Progress
            </Typography>
            <Typography variant="caption" sx={{ color: '#A78BFA', fontWeight: 700 }}>
              {Math.round(progressPercent)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={isProcessing ? Math.max(progressPercent, 5) : progressPercent}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: (theme) => alpha('#8B5CF6', theme.palette.mode === 'dark' ? 0.15 : 0.08),
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: allCompleted
                  ? 'linear-gradient(90deg, #10B981 0%, #34D399 100%)'
                  : `linear-gradient(90deg, #8B5CF6, #6D28D9, #3B82F6, #8B5CF6)`,
                backgroundSize: allCompleted ? '100% 100%' : '300% 100%',
                animation: allCompleted ? 'none' : `${shimmer} 2s linear infinite`,
                transition: 'width 0.5s ease',
              },
            }}
          />
        </Box>

        {/* Steps Timeline */}
        <Box sx={{ px: 3, pb: 2 }}>
          {steps.map((step, index) => {
            const isActive = step.status === 'running';
            const isDone = step.status === 'completed';
            const isError = step.status === 'error';
            const isPending = step.status === 'pending';

            return (
              <Grow in key={step.label} timeout={(index + 1) * 200}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mb: index < steps.length - 1 ? 0 : 0,
                    position: 'relative',
                  }}
                >
                  {/* Timeline connector */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 32 }}>
                    {/* Step circle */}
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid',
                        borderColor: isDone
                          ? '#10B981'
                          : isActive
                          ? '#8B5CF6'
                          : isError
                          ? '#EF4444'
                          : alpha('#64748B', 0.3),
                        backgroundColor: isDone
                          ? alpha('#10B981', 0.15)
                          : isActive
                          ? alpha('#8B5CF6', 0.15)
                          : isError
                          ? alpha('#EF4444', 0.15)
                          : 'transparent',
                        transition: 'all 0.4s ease',
                        animation: isActive ? `${pulseGlow} 2s ease-in-out infinite` : 'none',
                        zIndex: 1,
                      }}
                    >
                      {isDone ? (
                        <CompletedIcon sx={{ color: '#10B981', fontSize: 18 }} />
                      ) : isError ? (
                        <ErrorIcon sx={{ color: '#EF4444', fontSize: 18 }} />
                      ) : isActive ? (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#8B5CF6',
                            animation: `${dotPulse} 1.4s ease-in-out infinite`,
                          }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', fontSize: '0.7rem' }}>
                          {index + 1}
                        </Typography>
                      )}
                    </Box>
                    {/* Connecting line */}
                    {index < steps.length - 1 && (
                      <Box
                        sx={{
                          width: 2,
                          flex: 1,
                          minHeight: 24,
                          backgroundColor: isDone
                            ? alpha('#10B981', 0.4)
                            : alpha('#64748B', 0.15),
                          transition: 'background-color 0.5s ease',
                        }}
                      />
                    )}
                  </Box>

                  {/* Step content */}
                  <Box sx={{ flex: 1, pb: index < steps.length - 1 ? 2 : 0, pt: 0.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: isActive ? 700 : isDone ? 600 : 500,
                          color: isDone
                            ? '#10B981'
                            : isActive
                            ? '#A78BFA'
                            : isError
                            ? '#EF4444'
                            : 'text.secondary',
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {step.label}
                      </Typography>
                      {isActive && (
                        <Chip
                          label="PROCESSING"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.6rem',
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            backgroundColor: alpha('#8B5CF6', 0.2),
                            color: '#A78BFA',
                            animation: `${fadeSlideIn} 0.3s ease`,
                          }}
                        />
                      )}
                      {isDone && step.duration && (
                        <Typography variant="caption" sx={{ color: alpha('#10B981', 0.7), fontWeight: 500 }}>
                          {step.duration}s
                        </Typography>
                      )}
                      {isError && (
                        <Chip
                          label="FAILED"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.6rem',
                            fontWeight: 800,
                            backgroundColor: alpha('#EF4444', 0.2),
                            color: '#EF4444',
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mt: 0.25,
                        opacity: isPending ? 0.5 : 0.8,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Box>
              </Grow>
            );
          })}
        </Box>

        {/* Live Logs Terminal */}
        {logLines.length > 0 && (
          <Fade in timeout={500}>
            <Box
              sx={{
                mx: 3,
                mb: 2.5,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: alpha('#8B5CF6', 0.15),
              }}
            >
              {/* Terminal header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  backgroundColor: alpha('#1E1B4B', 0.6),
                  borderBottom: '1px solid',
                  borderColor: alpha('#8B5CF6', 0.1),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LogIcon sx={{ fontSize: 14, color: '#A78BFA' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#A78BFA', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                    AGENT PROCESSING LOG
                  </Typography>
                  {isProcessing && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: '#10B981',
                        animation: `${dotPulse} 1.4s ease-in-out infinite`,
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>
                  {logLines.length} entries
                </Typography>
              </Box>

              {/* Log content */}
              <Box
                ref={logContainerRef}
                sx={{
                  maxHeight: 180,
                  overflowY: 'auto',
                  p: 1.5,
                  backgroundColor: alpha('#0F0B1A', 0.8),
                  fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
                  fontSize: '0.68rem',
                  lineHeight: 1.7,
                  '&::-webkit-scrollbar': {
                    width: 4,
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha('#8B5CF6', 0.3),
                    borderRadius: 2,
                  },
                }}
              >
                {logLines.map((line, i) => {
                  const isError = line.includes('[ERROR]') || line.includes('FAILED');
                  const isDone = line.includes('[DONE]') || line.includes('✅');
                  const isOrchestrator = line.includes('Orchestrator');
                  const isDocAgent = line.includes('Document Agent');
                  const isOCRAgent = line.includes('OCR Extraction Agent');
                  const isERPAgent = line.includes('ERP Comparison Agent');
                  const isClassAgent = line.includes('Classification Agent');
                  const isHandoff = line.includes('Handoff:');

                  let color = '#94A3B8';
                  if (isError) color = '#EF4444';
                  else if (isDone) color = '#10B981';
                  else if (isHandoff) color = '#F59E0B';
                  else if (isOrchestrator) color = '#C084FC';
                  else if (isDocAgent) color = '#60A5FA';
                  else if (isOCRAgent) color = '#34D399';
                  else if (isERPAgent) color = '#FBBF24';
                  else if (isClassAgent) color = '#F472B6';

                  return (
                    <Box
                      key={i}
                      sx={{
                        animation: `${fadeSlideIn} 0.3s ease`,
                        py: 0.15,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          color: alpha('#8B5CF6', 0.5),
                          fontSize: 'inherit',
                          fontFamily: 'inherit',
                          mr: 1,
                        }}
                      >
                        {'>'}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color,
                          fontSize: 'inherit',
                          fontFamily: 'inherit',
                          fontWeight: isDone ? 600 : 400,
                        }}
                      >
                        {line}
                      </Typography>
                    </Box>
                  );
                })}
                {isProcessing && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', mt: 0.5 }}>
                    <Typography component="span" sx={{ color: alpha('#8B5CF6', 0.5), fontSize: '0.68rem', fontFamily: 'inherit' }}>
                      {'>'}
                    </Typography>
                    {[0, 1, 2].map(i => (
                      <Box
                        key={i}
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          backgroundColor: '#8B5CF6',
                          animation: `${dotPulse} 1.4s ease-in-out ${i * 0.2}s infinite`,
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Fade>
        )}
      </Box>
    </Fade>
  );
};

export default ProcessingStatus;
